import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Card,
  CardContent,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  FormLabel,
  Button,
  Alert,
  Chip,
  CircularProgress,
  Divider
} from '@mui/material';
import LanguageIcon from '@mui/icons-material/Language';
import SaveIcon from '@mui/icons-material/Save';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useAuth as _useAuth } from '../../contexts/AuthContext';
import { useLanguageAwareRender } from '../../hooks/useLanguageAwareRender';
import { t } from '../../utils/localization/localization';
import { createErrorLogger, ErrorType } from '../../utils/logger';
import systemLanguageService from '../../services/systemLanguageService';
import { ErrorState as _ErrorState } from '../common';

const logger = createErrorLogger('SystemLanguageManagement');

export default function SystemLanguageManagement() {
  // Use custom hook for language-aware rendering
  const languageRenderKey = useLanguageAwareRender('SystemLanguageManagement');

  const [currentLanguage, setCurrentLanguage] = useState('english');
  const [selectedLanguage, setSelectedLanguage] = useState('english');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [lastUpdated, setLastUpdated] = useState(null);
  const [updatedBy, setUpdatedBy] = useState(null);

  /**
   * Load current system language setting
   */
  const loadSystemLanguage = async () => {
    logger.info('loadSystemLanguage', 'Loading current system language setting');
    setLoading(true);
    setError('');

    try {
      const languageData = await systemLanguageService.getSystemLanguage();

      setCurrentLanguage(languageData.language || 'english');
      setSelectedLanguage(languageData.language || 'english');
      setLastUpdated(languageData.timestamp);
      setUpdatedBy(languageData.updated_by);

      logger.info('loadSystemLanguage', 'System language loaded successfully', {
        language: languageData.language,
        timestamp: languageData.timestamp
      });
    } catch (error) {
      logger.error('loadSystemLanguage', 'Failed to load system language', { error: error.message }, ErrorType.NETWORK_ERROR);
      setError(t('admin.systemLanguage.errorLoading') || 'Failed to load system language setting');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Save system language setting
   */
  const handleSave = async () => {
    logger.info('handleSave', 'Attempting to save system language', {
      currentLanguage,
      selectedLanguage
    });

    // Check if there's actually a change
    if (selectedLanguage === currentLanguage) {
      setError(t('admin.systemLanguage.noChangeNeeded') || 'No change needed - language is already set to the selected value');
      return;
    }

    setSaving(true);
    setError('');
    setSuccess('');

    try {
      const updateResult = await systemLanguageService.setSystemLanguage(selectedLanguage);

      // Update local state
      setCurrentLanguage(updateResult.language);
      setLastUpdated(updateResult.timestamp);
      setUpdatedBy(updateResult.updated_by);

      setSuccess(
        t('admin.systemLanguage.updateSuccess', { language: getLanguageDisplayName(updateResult.language) }) ||
        `System language updated successfully to ${getLanguageDisplayName(updateResult.language)}`
      );

      logger.info('handleSave', 'System language updated successfully', {
        language: updateResult.language,
        updated_by: updateResult.updated_by,
        timestamp: updateResult.timestamp
      });

      // Clear success message after 5 seconds
      setTimeout(() => {
        setSuccess('');
      }, 5000);

    } catch (error) {
      logger.error('handleSave', 'Failed to save system language', {
        selectedLanguage,
        error: error.message
      }, ErrorType.NETWORK_ERROR);

      setError(error.message || t('admin.systemLanguage.errorSaving') || 'Failed to save system language setting');
    } finally {
      setSaving(false);
    }
  };

  /**
   * Refresh system language data
   */
  const handleRefresh = () => {
    logger.info('handleRefresh', 'Refreshing system language data');
    systemLanguageService.clearCache();
    loadSystemLanguage();
  };

  /**
   * Handle language selection change
   */
  const handleLanguageChange = (event) => {
    const newLanguage = event.target.value;
    logger.info('handleLanguageChange', 'Language selection changed', {
      from: selectedLanguage,
      to: newLanguage
    });

    setSelectedLanguage(newLanguage);
    setError(''); // Clear any previous errors
  };

  // Load system language on component mount
  useEffect(() => {
    loadSystemLanguage();
  }, []);

  // Format timestamp for display
  const formatTimestamp = (timestamp) => {
    if (!timestamp) return '';
    try {
      return new Date(timestamp).toLocaleString();
    } catch (error) {
      return timestamp;
    }
  };

  // Get language display name
  const getLanguageDisplayName = (language) => {
    if (language === 'french') {
      return t('admin.systemLanguage.french') || 'Français (French)';
    }
    return t('admin.systemLanguage.english') || 'English';
  };

  // Get language description
  const getLanguageDescription = (language) => {
    if (language === 'french') {
      return t('admin.systemLanguage.frenchDescription') || 'AI analysis outputs will be generated in French';
    }
    return t('admin.systemLanguage.englishDescription') || 'AI analysis outputs will be generated in English';
  };

  // Show loading state
  if (loading) {
    return (
      <Box key={languageRenderKey} sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>
          {t('admin.systemLanguage.loading') || 'Loading system language settings...'}
        </Typography>
      </Box>
    );
  }

  return (
    <Box key={languageRenderKey}>
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
        <LanguageIcon color="primary" />
        <Typography variant="h5">
          {t('admin.systemLanguage.title') || 'System Language Management'}
        </Typography>
        <Chip
          label={getLanguageDisplayName(currentLanguage)}
          color="primary"
          size="small"
          sx={{ ml: 1 }}
        />
      </Box>

      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        {t('admin.systemLanguage.description') || 'Configure the language used by the AI system for analysis outputs. This setting affects the language of CV analysis, job analysis, company research, and training recommendation results - not the interface language.'}
      </Typography>

      <Paper sx={{ p: 3, mb: 3 }}>
        <FormControl component="fieldset" fullWidth>
          <FormLabel component="legend" sx={{ mb: 2 }}>
            <Typography variant="h6">
              {t('admin.systemLanguage.selectLanguage') || 'Select System Language'}
            </Typography>
          </FormLabel>

          <RadioGroup
            value={selectedLanguage}
            onChange={handleLanguageChange}
            sx={{ gap: 2 }}
          >
            <Card variant="outlined" sx={{ border: selectedLanguage === 'english' ? 2 : 1, borderColor: selectedLanguage === 'english' ? 'primary.main' : 'divider' }}>
              <CardContent>
                <FormControlLabel
                  value="english"
                  control={<Radio />}
                  label={
                    <Box>
                      <Typography variant="subtitle1" fontWeight="bold">
                        {getLanguageDisplayName('english')}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {getLanguageDescription('english')}
                      </Typography>
                    </Box>
                  }
                  sx={{ margin: 0, width: '100%', alignItems: 'flex-start' }}
                />
              </CardContent>
            </Card>

            <Card variant="outlined" sx={{ border: selectedLanguage === 'french' ? 2 : 1, borderColor: selectedLanguage === 'french' ? 'primary.main' : 'divider' }}>
              <CardContent>
                <FormControlLabel
                  value="french"
                  control={<Radio />}
                  label={
                    <Box>
                      <Typography variant="subtitle1" fontWeight="bold">
                        {getLanguageDisplayName('french')}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {getLanguageDescription('french')}
                      </Typography>
                    </Box>
                  }
                  sx={{ margin: 0, width: '100%', alignItems: 'flex-start' }}
                />
              </CardContent>
            </Card>
          </RadioGroup>
        </FormControl>

        <Divider sx={{ my: 3 }} />

        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <Button
            variant="contained"
            startIcon={saving ? <CircularProgress size={16} /> : <SaveIcon />}
            onClick={handleSave}
            disabled={saving || selectedLanguage === currentLanguage}
            size="large"
          >
            {saving ? (t('admin.systemLanguage.saving') || 'Saving...') : (t('common.save') || 'Save Changes')}
          </Button>

          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={handleRefresh}
            disabled={saving}
          >
            {t('common.refresh') || 'Refresh'}
          </Button>
        </Box>
      </Paper>

      {/* Status Information */}
      <Paper sx={{ p: 2 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          {t('admin.systemLanguage.currentStatus') || 'Current Status'}
        </Typography>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Typography variant="body2" color="text.secondary">
              {t('admin.systemLanguage.currentLanguage') || 'Current Language:'}
            </Typography>
            <Chip
              label={getLanguageDisplayName(currentLanguage)}
              size="small"
              color="primary"
            />
          </Box>

          {lastUpdated && (
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Typography variant="body2" color="text.secondary">
                {t('admin.systemLanguage.lastUpdated') || 'Last Updated:'}
              </Typography>
              <Typography variant="body2">
                {formatTimestamp(lastUpdated)}
              </Typography>
            </Box>
          )}

          {updatedBy && (
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Typography variant="body2" color="text.secondary">
                {t('admin.systemLanguage.updatedBy') || 'Updated By:'}
              </Typography>
              <Typography variant="body2">
                {updatedBy}
              </Typography>
            </Box>
          )}
        </Box>
      </Paper>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mt: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* Success Alert */}
      {success && (
        <Alert severity="success" sx={{ mt: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}
    </Box>
  );
}